import * as THREE from "three";
import { LOOKS, buildEnvironment } from "../look";
import { buildTowerModel } from "../views/towerModel";
import { lib } from "../views/common";
import { SkinKits } from "./kit/runtime";
import { WornSkins } from "./wornSkins";
import { SkinThumbs } from "./skinThumb";
import { SKIN_LOOKS, STOCK_LOOKS } from "./looks";

/**
 * The skins' readability sheet (FINISH_PLAN WP8 acceptance: "a capture sheet of every skin at game distance"; debug
 * only, More → Debug: skin sheet, or `__tdSkins.sheet()`): every tower type in every tower skin, then the fortress,
 * troop skins, each drawn at the size a tower has on a phone at the level's opening zoom and from the game camera's
 * angle, so a skin that loses what a tower is — its silhouette, its element glow — shows here before it ships.
 */
export class ReadabilitySheet {
  /** One cell, in px: a tier-2 tower is ~50 CSS px tall at the opening zoom (about seven cells across a phone). */
  static readonly Cell = 72;
  static readonly LabelW = 74;
  static readonly HeadH = 28;
  /** The game camera: 35° down, the map's 45° turn; the tower rows from its own side (SkinThumbs.viewDir). */
  static readonly TiltDeg = 35;
  static readonly YawDeg = 45;
  static readonly Tier = 2;
  /** Half-extents of a cell's view in world units: a tower's cell, the 3 × 2 fortress, a troop (their sizes on the map). */
  static readonly TowerHalf = 1.1;
  static readonly FortressHalf = 2.2;
  static readonly TroopHalf = 0.3;

  static async render(towerIds: readonly string[]): Promise<HTMLCanvasElement> {
    const L = lib();
    const C = ReadabilitySheet.Cell, T = ReadabilitySheet.Tier;
    const towerSkins: (string | null)[] = [null, ...Object.keys(SKIN_LOOKS).filter((id) => id.startsWith("tower."))];
    const others: { label: string; kind: "fortress" | "troop"; skins: (string | null)[] }[] = [
      { label: "Fortress", kind: "fortress", skins: [null, ...Object.keys(SKIN_LOOKS).filter((id) => id.startsWith("fortress."))] },
      { label: "Troop", kind: "troop", skins: [null, ...Object.keys(SKIN_LOOKS).filter((id) => id.startsWith("troop."))] },
    ];
    const out = document.createElement("canvas");
    out.width = ReadabilitySheet.LabelW + C * towerSkins.length;
    out.height = ReadabilitySheet.HeadH + C * (towerIds.length + others.length);
    const g = out.getContext("2d")!;
    g.fillStyle = "#4f7a3a"; g.fillRect(0, 0, out.width, out.height);             // turf-ish: the colour they stand on
    g.fillStyle = "#fff8e6"; g.font = "bold 10px 'Fredoka Display', sans-serif"; g.textBaseline = "middle";
    towerSkins.forEach((id, i) => g.fillText((id ? SKIN_LOOKS[id].name : STOCK_LOOKS.tower.name).slice(0, 11), ReadabilitySheet.LabelW + i * C + 4, ReadabilitySheet.HeadH / 2));
    if (!L) return out;
    const troops = others[1].skins.map((id) => WornSkins.troopModel(id));
    const kits = towerSkins.map((id) => SkinKits.towerKit(id)?.asset).filter((a): a is string => !!a);
    // big kits' own tower models, where they ship
    const own = towerIds.flatMap((t) => towerSkins.map((id) => WornSkins.towerModelOf(id)).filter((m): m is string => !!m).map((m) => `tower_${t}_t${T}__${m}`)).filter((n) => L.ships(n));
    const names = [...towerIds.map((t) => `tower_${t}_t${T}`), ...own, `base_t${T}`, `base_t${T}__dragonlord`, ...troops, ...kits];
    await L.ensure(names);
    const r = new THREE.WebGLRenderer({ alpha: true, antialias: true, preserveDrawingBuffer: true });
    r.setSize(C, C, false);
    r.setClearAlpha(0);
    r.outputColorSpace = THREE.SRGBColorSpace;
    r.toneMapping = THREE.ACESFilmicToneMapping;
    const look = LOOKS.painterly;
    r.toneMappingExposure = look.exposure;
    const scene = new THREE.Scene();
    scene.environment = buildEnvironment(r, look);
    const key = new THREE.DirectionalLight(look.sun.color, look.sun.intensity);
    key.position.set(...SkinThumbs.KeyAt);
    scene.add(key, new THREE.HemisphereLight(look.hemi.sky, look.hemi.ground, look.hemi.intensity));
    const cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.01, 100);
    /** Every model in a row shares one frame, so a skin that changes the size or shape would show it. */
    const draw = (root: THREE.Object3D, half: number, x: number, y: number, gameSide: boolean) => {
      scene.add(root);
      root.updateMatrixWorld(true);
      const turn = gameSide ? -1 : 1;
      key.position.set(SkinThumbs.KeyAt[0] * turn, SkinThumbs.KeyAt[1], SkinThumbs.KeyAt[2] * turn);
      const box = new THREE.Box3().setFromObject(root), c = box.getCenter(new THREE.Vector3());
      cam.position.copy(c).addScaledVector(SkinThumbs.viewDir(ReadabilitySheet.TiltDeg, ReadabilitySheet.YawDeg, gameSide), 20);
      cam.lookAt(c);
      cam.left = -half; cam.right = half; cam.top = half; cam.bottom = -half;
      cam.updateProjectionMatrix();
      r.render(scene, cam);
      g.drawImage(r.domElement, x, y, C, C);
      scene.remove(root);
    };
    const row = (label: string, rowIndex: number, cells: { build: () => THREE.Object3D }[], half: number, gameSide = false) => {
      const y = ReadabilitySheet.HeadH + rowIndex * C;
      g.fillStyle = "#fff8e6"; g.fillText(label.slice(0, 11), 4, y + C / 2);
      cells.forEach((cell, i) => draw(cell.build(), half, ReadabilitySheet.LabelW + i * C, y, gameSide));
    };
    towerIds.forEach((t, i) => row(t, i, towerSkins.map((id) => ({ build: () => { const m = buildTowerModel(t, false, T, WornSkins.towerModelOf(id)); WornSkins.dressTower(m.storeys, t, false, id); return m.storeys; } })), ReadabilitySheet.TowerHalf, true));
    others.forEach((o, j) => row(o.label, towerIds.length + j, o.skins.map((id) => ({
      build: () => {
        if (o.kind === "troop") { const b = L.clone(`hq:${WornSkins.troopModel(id)}`); WornSkins.dressTroop(b, id); return b; }
        const m = buildTowerModel("base", true, T, WornSkins.modelOf(id)); WornSkins.dressTower(m.storeys, "base", true, id); return m.storeys;
      },
    })), o.kind === "troop" ? ReadabilitySheet.TroopHalf : ReadabilitySheet.FortressHalf));
    scene.environment?.dispose();
    r.dispose();
    r.forceContextLoss();
    return out;
  }
}
